"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useEffect, useMemo, useState } from "react"
import { ArrowDown, ArrowUp, ArrowUpDown, Boxes, Loader2, Search } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Pagination } from "@/components/ui/pagination"
import {
  useCatalogCollections,
  useCatalogFilterOptions,
  useCatalogProducts,
  type CatalogProduct,
  type CatalogSortKey,
  type CatalogSortOrder,
} from "./use-catalog-products"
import { CatalogStatusFilter } from "./catalog-status-filter"
import { InitialSyncHint } from "@/components/shopify/initial-sync-hint"
import {
  useCatalogSyncCompletionRefresh,
  useInitialSyncPart,
  useSyncCompletionRefresh,
} from "@/hooks/queries/shopify-initial-sync"
import {
  MultiSelectFilter,
  type MultiSelectOption,
} from "@/components/ui/multi-select-filter"
import { useTranslations } from "next-intl"



interface CatalogOverviewTabProps {
  integrationId: string
}

const PAGE_SIZE_DEFAULT = 25
const SEARCH_DEBOUNCE_MS = 250
/**
 * Sentinel for "no filter" in the Select dropdowns. Radix `<Select>` cannot use
 * an empty-string value, so we use a sentinel and translate it to `null` when
 * passing to the hook. Keep in sync — these MUST be unique per column.
 */
const FILTER_ALL = "__all__"

function formatLastSold(value: string | null, dateLocale: string = "en-US"): string {
  if (!value) return "—"
  try {
    return new Date(value).toLocaleDateString(dateLocale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch {
    return "—"
  }
}

function statusBadgeVariant(
  status: string | null,
): "default" | "secondary" | "outline" {
  if (!status) return "outline"
  const normalized = status.toLowerCase()
  if (normalized === "active") return "default"
  if (normalized === "archived") return "secondary"
  return "outline"
}

/**
 * Full catalog browser — every product mirrored from Shopify, including SKUs
 * that have never been sold. Backed by `/api/integrations/shopify/products-catalog`
 * (RPC `get_products_catalog`).
 *
 * Filters (status / vendor / category) and sort happen on the server so they
 * compose correctly with pagination — a client-side filter would only see the
 * current page and break `total` semantics.
 */
export function CatalogOverviewTab({ integrationId }: CatalogOverviewTabProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()


  const tUi = useTranslations("Shopify.ShopifyCatalogCatalogOverviewTab")

  const initialSync = useInitialSyncPart("catalog", integrationId)
  const [searchInput, setSearchInput] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)
  // Default to ACTIVE — operators almost always want to see the live catalog
  // first; archived / draft / unlisted are explicit opt-in via the multi-select.
  // Uppercase matches the Shopify ProductStatus enum the bulk sync stores verbatim.
  const [statuses, setStatuses] = useState<string[]>(["ACTIVE"])
  const [vendor, setVendor] = useState<string | null>(null)
  const [taxonomyCategoryIds, setTaxonomyCategoryIds] = useState<string[]>([])
  const [collectionIds, setCollectionIds] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<CatalogSortKey>("title")
  const [sortOrder, setSortOrder] = useState<CatalogSortOrder>("asc")

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchInput])

  // Reset to page 1 whenever the result set changes (search, filters, sort).
  // Otherwise the user could be on page 4 with no matching rows.
  const statusesKey = statuses.slice().sort().join("|")
  const categoryIdsKey = taxonomyCategoryIds.slice().sort().join("|")
  const collectionIdsKey = collectionIds.slice().sort().join("|")
  useEffect(() => {
    setPage(1)
    // Array refs covered by their stable keys above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    statusesKey,
    vendor,
    categoryIdsKey,
    collectionIdsKey,
    sortBy,
    sortOrder,
    pageSize,
  ])

  const { options: filterOptions } = useCatalogFilterOptions(integrationId)
  // Collections dropdown — same hook used by the Collections tab; pinned with
  // empty statuses so the operator can see every collection regardless of the
  // current Status filter (a collection of DRAFT-only products is still a
  // valid filter target when narrowing Status to DRAFT).
  const { collections: allCollections } = useCatalogCollections(integrationId, { statuses: [] })

  const categoryOptions = useMemo<MultiSelectOption[]>(
    () => filterOptions.categories.map((c) => ({ value: c.id, label: c.name })),
    [filterOptions.categories],
  )
  const collectionOptions = useMemo<MultiSelectOption[]>(
    () =>
      allCollections.map((c) => ({
        value: c.collection_id,
        label: c.title,
        subtitle: c.handle ? `/${c.handle}` : undefined,
      })),
    [allCollections],
  )

  const { products, total, isLoading, error: catalogError, reload } = useCatalogProducts({
    integrationId,
    page,
    limit: pageSize,
    search: debouncedSearch,
    statuses,
    vendor,
    taxonomyCategoryIds,
    collectionIds,
    sortBy,
    sortOrder,
  })

  // Same reason as the orders page: this list is not React Query, so the status poll
  // cannot invalidate it when the first catalog bulk lands.
  useSyncCompletionRefresh("catalog", reload, integrationId)
  useCatalogSyncCompletionRefresh(reload, integrationId)

  const handleSort = (column: CatalogSortKey) => {
    if (column === sortBy) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(column)
      setSortOrder("asc")
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={tUi("searchByTitleOrSKU")}
            className="pl-8"
          />
        </div>
        <CatalogStatusFilter
          availableStatuses={filterOptions.statuses}
          selectedStatuses={statuses}
          onChange={setStatuses}
        />

        <Select
          value={vendor ?? FILTER_ALL}
          onValueChange={(v) => setVendor(v === FILTER_ALL ? null : v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={tUi("vendor")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ALL}><span key={tUi("allVendors")}>{tUi("allVendors")}</span></SelectItem>
            {filterOptions.vendors.map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <MultiSelectFilter
          labelPrefix="Category"
          pluralNoun="categories"
          availableOptions={categoryOptions}
          selectedValues={taxonomyCategoryIds}
          onChange={setTaxonomyCategoryIds}
        />
        <MultiSelectFilter
          labelPrefix="Collection"
          pluralNoun="collections"
          availableOptions={collectionOptions}
          selectedValues={collectionIds}
          onChange={setCollectionIds}
        />
        <div key={isLoading ? tUi("loading") : tUi("value0Products", { value0: String(total.toLocaleString(uiFormattingLocale)) })} className="text-sm text-muted-foreground ml-auto">
          {isLoading ? tUi("loading") : tUi("value0Products", { value0: String(total.toLocaleString(uiFormattingLocale)) })}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading && products.length === 0 ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span><span key={tUi("loadingCatalog")}>{tUi("loadingCatalog")}</span></span>
            </div>
          ) : catalogError ? (
            <div className="py-16 flex flex-col items-center text-center gap-3">
              <Boxes className="h-10 w-10 text-destructive/60" />
              <div className="max-w-sm text-sm text-destructive">{catalogError}</div>
              <div className="max-w-sm text-xs text-muted-foreground">
                <span key={tUi("adjustTheFiltersOrRetryThePreviousResult")}>{tUi("adjustTheFiltersOrRetryThePreviousResult")}</span></div>
            </div>
          ) : products.length === 0 ? (
            <div className="py-16 flex flex-col items-center text-center text-muted-foreground gap-3">
              <Boxes className="h-10 w-10 text-muted-foreground/60" />
              <div className="max-w-sm text-sm">
                {debouncedSearch ||
                statuses.length > 0 ||
                vendor ||
                taxonomyCategoryIds.length > 0 ||
                collectionIds.length > 0 ? (
                  tUi("noProductsMatchTheCurrentFilters")
                ) : initialSync ? (
                  <InitialSyncHint part="catalog" integrationId={integrationId} />
                ) : (
                  tUi("catalogIsEmptyRunAShopifySyncTo")
                )}
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <SortableHeader
                      label={tUi("product")}
                      column="title"
                      activeColumn={sortBy}
                      order={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeader
                      label={tUi("status")}
                      column="status"
                      activeColumn={sortBy}
                      order={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeader
                      label={tUi("vendor")}
                      column="vendor"
                      activeColumn={sortBy}
                      order={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeader
                      label={tUi("category")}
                      column="category"
                      activeColumn={sortBy}
                      order={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    <SortableHeader
                      label={tUi("variants")}
                      column="variants_count"
                      activeColumn={sortBy}
                      order={sortOrder}
                      onSort={handleSort}
                      align="right"
                    />
                  </TableHead>
                  <TableHead>
                    <SortableHeader
                      label={tUi("lastSold")}
                      column="last_sold_at"
                      activeColumn={sortBy}
                      order={sortOrder}
                      onSort={handleSort}
                    />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <CatalogOverviewRow key={p.product_id} product={p} />
                ))}
              </TableBody>
            </Table>
          )}
          {products.length > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Sortable header button. Single source of truth for the icon switch so every
 * column reads / behaves identically; matches the pattern used on the orders
 * page (`components/shopify/orders-page.tsx`).
 */
function SortableHeader({
  label,
  column,
  activeColumn,
  order,
  onSort,
  align = "left",
}: {
  label: string
  column: CatalogSortKey
  activeColumn: CatalogSortKey
  order: CatalogSortOrder
  onSort: (column: CatalogSortKey) => void
  align?: "left" | "right"
}) {
  const isActive = activeColumn === column
  return (
    <Button
      variant="ghost"
      onClick={() => onSort(column)}
      className={`h-8 px-2 ${align === "right" ? "ml-auto" : ""}`}
    >
      {label}
      {isActive ? (
        order === "asc" ? (
          <ArrowUp className="ml-1 h-4 w-4" />
        ) : (
          <ArrowDown className="ml-1 h-4 w-4" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />
      )}
    </Button>
  )
}

function CatalogOverviewRow({ product }: { product: CatalogProduct }) {
  const {locale: uiDateLocale} = useUiControlLabels()

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{product.title}</div>
        {product.sku && (
          <div className="text-xs text-muted-foreground"><span><span key={"SKU:"}>{"SKU:"}</span></span> {product.sku}</div>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={statusBadgeVariant(product.status)} className="capitalize">
          {product.status?.toLowerCase() ?? "—"}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{product.vendor ?? "—"}</TableCell>
      <TableCell className="text-sm">
        {product.taxonomy_category_name ?? product.product_type ?? "—"}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {product.variants_count}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatLastSold(product.last_sold_at, uiDateLocale)}
      </TableCell>
    </TableRow>
  )
}
